// Shapes for the blocking "Scorecard required" modal: the structured 422 the
// monolith renders when the "Require scorecard before advancing or rejecting"
// stage setting refuses a move, reject, or bulk action.

import { EvaluatorStatus } from 'containers/ScorecardModals/InviteEvaluators/inviteEvaluators';

// Mirrored against ScoreCards::AdvancementRefusal::ERROR_TYPE in apps/hire
// through tools/contract-mirrors/mirrors.json.
export const SCORECARD_REQUIRED_ERROR_TYPE = 'scorecard_required';

export type RequiredSubmissions =
  | 'all_evaluators'
  | 'at_least_one'
  | 'specific_number';

export interface ScorecardRequirementEvaluator {
  // The evaluator's external id.
  user: { id: string | null; name: string | null };
  template: { id: number; name: string };
  status: EvaluatorStatus;
  // True on the derived default owner, who holds no stored invitation.
  default: boolean;
  // Whether Send reminder is offered: the reminder rides on a stored
  // invitation, a submitted scorecard refuses it, and so does a flag
  // rollout that excludes the evaluator.
  remindable: boolean;
}

export interface ScorecardRequirementApplicant {
  // The applicant's external id.
  id: string;
  name: string | null;
  submitted_count: number; // eslint-disable-line camelcase
  required_count: number | null; // eslint-disable-line camelcase
  evaluators: ScorecardRequirementEvaluator[];
}

export interface ScorecardRequirement {
  stage: { id: string; name: string };
  required_submissions: RequiredSubmissions; // eslint-disable-line camelcase
  required_submissions_count: number | null; // eslint-disable-line camelcase
  // The true total; `blocked_applicants` is capped server-side.
  blocked_count: number; // eslint-disable-line camelcase
  blocked_applicants: ScorecardRequirementApplicant[]; // eslint-disable-line camelcase
}

// Why the modal opened on a saved action rather than a refused one: the
// action went through, and only the advance it would trigger waits on the
// scorecards.
export type ScorecardRequiredNotice = 'attendance_saved';

// What the axios interceptor rejects with when the response body carries
// `error_type: 'scorecard_required'`.
export interface ScorecardRequiredRejection {
  errorType: typeof SCORECARD_REQUIRED_ERROR_TYPE;
  message: string;
  scorecardRequirement: ScorecardRequirement | null;
}

export function isScorecardRequiredRejection(
  value: unknown,
): value is ScorecardRequiredRejection & {
  scorecardRequirement: ScorecardRequirement;
} {
  if (value == null || typeof value !== 'object') {
    return false;
  }
  const candidate = value as {
    errorType?: unknown;
    scorecardRequirement?: unknown;
  };
  return (
    candidate.errorType === SCORECARD_REQUIRED_ERROR_TYPE &&
    candidate.scorecardRequirement != null &&
    typeof candidate.scorecardRequirement === 'object'
  );
}
